import React, { useCallback, useEffect, useRef, useState } from 'react'
import { absoluteApiV1Path } from '../lib/api'
import { DialogBackdrop, DialogPanel } from '../ui/DialogLayout'

function inlineManualCss(text, css) {
  if (!css) return text
  const styleTag = `<style data-sgez-manual-css>\n${css}\n</style>`
  const withoutLink = String(text || '').replace(/<link\s+rel=["']stylesheet["']\s+href=["']sgez-manual\.css["']\s*\/?>/i, '')
  if (withoutLink.includes('</head>')) {
    return withoutLink.replace('</head>', `${styleTag}\n</head>`)
  }
  return `${styleTag}\n${withoutLink}`
}

export function prepareManualHtml(text, css, assetBaseUrl, { continuous = false } = {}) {
  const assetBase = String(assetBaseUrl || '').replace(/\/?$/, '/')
  let next = String(text || '')
  const modalCss = `
html {
  scroll-padding-top: 96px;
}

.manual-toc {
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.manual-toc a,
.manual-toc button {
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}

.manual-toc a.is-active,
.manual-toc button.is-active {
  border-color: var(--manual-brand);
  background: var(--manual-brand-soft);
  color: var(--manual-brand);
  font-weight: 800;
}
`

  const continuousCss = continuous ? `
body {
  background: #fff;
}

.sgez-manual {
  width: min(860px, calc(100% - 48px));
  padding: 32px 0 80px;
}

.manual-toc,
.manual-stepper {
  display: none !important;
}

.sgez-manual[data-stepper-ready="true"] .manual-step,
.manual-step,
.manual-step.is-active {
  display: block !important;
}

.manual-step {
  min-height: 0;
  margin-top: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0 0 48px;
}

.manual-step + .manual-step,
.manual-step + .manual-section,
.manual-section + .manual-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 48px;
}

.manual-step__header {
  padding-right: 0;
  margin-bottom: 18px;
}

.manual-step__count-badge {
  display: none !important;
}


.manual-step__layout,
.manual-step__layout.has-screen-callouts {
  display: block !important;
}

.manual-step__media {
  min-width: 0;
}

.manual-step__media .manual-figure {
  margin: 0;
}

.manual-step__media .manual-figure img,
.manual-step__layout.has-screen-callouts .manual-step__media .manual-figure img {
  display: block;
  width: 100%;
  max-height: none;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.manual-screen-callouts,
.manual-step__layout.has-screen-callouts .manual-screen-callouts {
  display: none !important;
}

.manual-step__copy,
.manual-step__layout.has-screen-callouts .manual-step__copy {
  display: block !important;
  margin-top: 18px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.manual-step__copy-title {
  margin: 0 0 10px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
}

.manual-step__copy .manual-ordered,
.manual-step__copy .manual-checklist {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: guide-action;
}

.manual-step__copy .manual-ordered li,
.manual-step__copy .manual-checklist li {
  position: relative;
  margin: 9px 0;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 0 0 24px !important;
  color: #334155;
  line-height: 1.75;
  counter-increment: guide-action;
}

.manual-step__copy .manual-ordered,
.manual-step__copy .manual-checklist {
  margin: 0 0 0 1.4rem !important;
  padding: 0 !important;
  list-style: decimal !important;
}

.manual-step__copy .manual-ordered li,
.manual-step__copy .manual-checklist li {
  display: list-item !important;
  padding: 0 !important;
}

.manual-step__copy .manual-ordered li::before,
.manual-step__copy .manual-checklist li::before {
  content: none !important;
  display: none !important;
}

.manual-section {
  margin-top: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding-bottom: 48px;
}

@media (max-width: 720px) {
  .sgez-manual {
    width: min(100% - 28px, 860px);
    padding: 24px 0 56px;
  }

  .manual-step,
  .manual-section {
    padding-bottom: 36px;
  }

  .manual-step + .manual-step,
  .manual-step + .manual-section,
  .manual-section + .manual-section {
    padding-top: 36px;
  }
}
` : ''
  const continuousScript = continuous ? `
<script data-sgez-manual-continuous>
  (() => {
    const root = document.querySelector('.sgez-manual')
    if (!root) return
    root.removeAttribute('data-stepper-ready')
    const stepper = root.querySelector('.manual-stepper')
    const track = stepper?.querySelector('.manual-stepper__track')
    if (stepper && track) {
      const fragment = document.createDocumentFragment()
      while (track.firstChild) fragment.appendChild(track.firstChild)
      stepper.parentNode?.insertBefore(fragment, stepper)
    }
    stepper?.remove()
    root.querySelector('.manual-toc')?.remove()
    root.querySelectorAll('.manual-step').forEach((step, index) => {
      step.hidden = false
      step.removeAttribute('aria-hidden')
      step.classList.add('is-active')

      const copyTitle = step.querySelector('.manual-step__copy-title')
      if (copyTitle) copyTitle.textContent = '이렇게 진행하세요'
    })
  })()
</script>` : ''
  // In srcDoc, <base> makes "#step" links resolve as external URLs.
  // Remove it and rewrite only asset URLs that actually need the BE origin.
  next = next.replace(/<base\b[^>]*>/i, '')
  next = next.replace(/(<img\b[^>]*\bsrc=["'])images\//gi, `$1${assetBase}images/`)
  next = next.replace(/(<a\b[^>]*\bhref=["'])(manual_[^"']+\.html)(["'])/gi, `$1${assetBase}$2$3`)

  // The containing view already has the document title. Start from the guide body.
  next = next.replace(/<header\b[^>]*class=["']manual-hero["'][^>]*>[\s\S]*?<\/header>\s*/i, '')
  next = next.replace(/<section\b[^>]*class=["']manual-section["'][^>]*>[\s\S]*?<\/section>\s*(?=<nav\b[^>]*class=["']manual-toc["'])/i, '')

  if (continuous && continuousScript) {
    next = next.includes('</body>')
      ? next.replace('</body>', `${continuousScript}\n</body>`)
      : `${next}\n${continuousScript}`
  }

  return inlineManualCss(next, `${css}\n${modalCss}\n${continuousCss}`)
}

export function SupportGuideManualContent({
  slug,
  title,
  anchor = '',
  className = '',
  iframeClassName = 'h-full w-full border-0 bg-white',
  continuous = false,
  fitContent = false,
  onTableOfContentsChange = null,
}) {
  const iframeRef = useRef(null)
  const [html, setHtml] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [contentHeight, setContentHeight] = useState(800)

  const htmlUrl = slug ? absoluteApiV1Path(`public/support-guide/manuals/by-slug/${slug}/html`) : ''
  const cssUrl = absoluteApiV1Path('public/support-guide/manual-assets/sgez-manual.css')
  const assetBaseUrl = absoluteApiV1Path('public/support-guide/manual-assets/')

  useEffect(() => {
    if (!slug || !htmlUrl) {
      setHtml('')
      setError('')
      setLoading(false)
      return
    }

    let cancelled = false
    setContentHeight(800)
    setLoading(true)
    setError('')

    Promise.all([
      fetch(htmlUrl, { credentials: 'omit' }).then((res) => {
        if (!res.ok) throw new Error(`HTML ${res.status}`)
        return res.text()
      }),
      fetch(cssUrl, { credentials: 'omit' }).then((res) => {
        if (!res.ok) return ''
        return res.text()
      }).catch(() => ''),
    ])
      .then(([text, css]) => {
        if (!cancelled) setHtml(prepareManualHtml(text, css, assetBaseUrl, { continuous }))
      })
      .catch(() => {
        if (!cancelled) setError('매뉴얼을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [slug, htmlUrl, cssUrl, assetBaseUrl, continuous])

  const scrollToManualAnchor = useCallback((targetAnchor) => {
    if (!targetAnchor) return
    try {
      const frame = iframeRef.current
      const doc = frame?.contentDocument
      const target = doc?.getElementById(targetAnchor)
      if (!target) return
      if (!continuous && target.classList.contains('manual-step')) {
        const stepIndex = Number(target.dataset.stepIndex || 0)
        const setStep = doc.defaultView?.sgezManualSetStep
        if (typeof setStep === 'function') {
          setStep(stepIndex)
          return
        }
      }
      if (fitContent && typeof window !== 'undefined' && frame) {
        const top = window.scrollY + frame.getBoundingClientRect().top + target.getBoundingClientRect().top - 88
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
        return
      }
      target.scrollIntoView({ block: 'start', behavior: 'smooth' })
    } catch {
      // srcDoc is same-origin, but keep the manual usable if a browser blocks access.
    }
  }, [continuous, fitContent])

  useEffect(() => {
    if (!html || !anchor) return undefined
    const frame = window.requestAnimationFrame(() => scrollToManualAnchor(anchor))
    return () => window.cancelAnimationFrame(frame)
  }, [anchor, html, scrollToManualAnchor])

  const handleIframeLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument
      if (doc) {
        if (!continuous && typeof doc.defaultView?.sgezManualSetStep === 'function') {
          scrollToManualAnchor(anchor)
          return
        }

        const tocLinks = Array.from(doc.querySelectorAll('.manual-toc a[href^="#"]'))
        const sections = tocLinks
          .map((link) => {
            const targetAnchor = String(link.getAttribute('href') || '').replace(/^#/, '')
            const target = doc.getElementById(targetAnchor)
            return target ? { link, target, targetAnchor } : null
          })
          .filter(Boolean)

        const setActiveLink = (targetAnchor) => {
          sections.forEach(({ link, targetAnchor: sectionAnchor }) => {
            link.classList.toggle('is-active', sectionAnchor === targetAnchor)
          })
        }

        tocLinks.forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault()
            const targetAnchor = String(link.getAttribute('href') || '').replace(/^#/, '')
            scrollToManualAnchor(targetAnchor)
            setActiveLink(targetAnchor)
          })
        })

        const onScroll = () => {
          const viewportTop = 100
          let current = sections[0]?.targetAnchor || ''
          sections.forEach(({ target, targetAnchor }) => {
            if (target.getBoundingClientRect().top <= viewportTop) {
              current = targetAnchor
            }
          })
          setActiveLink(current)
        }

        doc.addEventListener('scroll', onScroll, { passive: true })
        doc.defaultView?.addEventListener('scroll', onScroll, { passive: true })
        onScroll()

        if (continuous && typeof onTableOfContentsChange === 'function') {
          const tableOfContents = Array.from(doc.querySelectorAll('.manual-step, .manual-section'))
            .map((section, index) => {
              const isStep = section.classList.contains('manual-step')
              if (!section.id) section.id = `${isStep ? 'step' : 'section'}-${index + 1}`
              const heading = section.querySelector('.manual-step__header h2') || section.querySelector(':scope > h2')
              const title = heading?.textContent?.trim() || ''
              return title ? { id: section.id, title, kind: isStep ? 'step' : 'section' } : null
            })
            .filter(Boolean)
          onTableOfContentsChange(tableOfContents)
        }
      }
    } catch {
      // Ignore and keep native iframe behavior as fallback.
    }
    scrollToManualAnchor(anchor)

    if (fitContent) {
      try {
        const doc = iframeRef.current?.contentDocument
        const updateHeight = () => {
          const nextHeight = Math.max(
            800,
            Math.ceil(doc?.body?.scrollHeight || 0),
            Math.ceil(doc?.documentElement?.scrollHeight || 0),
          )
          setContentHeight((current) => (current === nextHeight ? current : nextHeight))
        }
        updateHeight()
        doc?.defaultView?.requestAnimationFrame(updateHeight)
      } catch {
        // Keep the initial height if the embedded document cannot be measured.
      }
    }
  }

  if (!slug) return null

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-white text-sm text-slate-500 ${className}`}>
        매뉴얼을 불러오는 중입니다.
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-white px-4 text-center text-sm text-rose-600 ${className}`}>
        {error}
      </div>
    )
  }

  return (
    <div className={`min-h-0 bg-white ${className}`}>
      <iframe
        ref={iframeRef}
        title={title || '이용 가이드'}
        srcDoc={html}
        onLoad={handleIframeLoad}
        className={iframeClassName}
        style={fitContent ? { height: `${contentHeight}px` } : undefined}
        scrolling={fitContent ? 'no' : undefined}
      />
    </div>
  )
}

export default function SupportGuideManualModal({ open, title, slug, anchor = '', onClose }) {
  const pdfUrl = slug ? absoluteApiV1Path(`public/support-guide/manuals/by-slug/${slug}/pdf`) : ''

  if (!open || !slug) return null

  return (
    <DialogBackdrop>
      <DialogPanel className="max-w-6xl" aria-label={title || '이용 가이드'}>
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-lg font-semibold text-slate-900">{title || '이용 가이드'}</div>
          </div>
          <button
            type="button"
            className="shrink-0 rounded border px-3 py-1 text-sm hover:bg-slate-50"
            onClick={onClose}
          >
            닫기
          </button>
        </div>
        <div className="min-h-0 flex-1 bg-slate-100">
          <SupportGuideManualContent
            slug={slug}
            title={title}
            anchor={anchor}
            className="h-[72vh]"
            iframeClassName="h-full w-full border-0 bg-white"
          />
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-white px-4 py-3">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            PDF 다운로드
          </a>
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
            onClick={onClose}
          >
            닫기
          </button>
        </div>
      </DialogPanel>
    </DialogBackdrop>
  )
}